/***************************************************************
5/22/2017
Alexander Self
Timesheet Reducer
- Takes timesheet actions and returns a new timesheet state
***************************************************************/
import moment from 'moment';
import { FETCH_TIMESHEET_DATA, ADD_EMPLOYEE_TO_TIMESHEET, REMOVE_EMPLOYEE_FROM_TIMESHEET,
  ADD_WORK_TO_TIMESHEET, EDIT_WORK_IN_TIMESHEET, REMOVE_WORK_FROM_TIMESHEET, ADD_EMPLOYEE_TO_GANG_TIMESHEET,
  REMOVE_EMPLOYEE_FROM_GANG_TIMESHEET, PREPARE_REG_TIMESHEET, PREPARE_GANG_TIMESHEET,
  SUBMIT_REG_TIMESHEET, SUBMIT_GANG_TIMESHEET, FETCH_TIMESHEETS, FETCH_MY_TIMESHEETS, GET_TIMESHEET_DATES_TO_EDIT,
  FETCH_TIMESHEET_TO_EDIT, REMOVE_WORK_FROM_EDIT_TIMESHEET, ADD_WORK_TO_EDIT_TIMESHEET,
  PREPARE_EDIT_TIMESHEET, SUBMIT_EDIT_TIMESHEET, FETCH_TIMESHEETS_TO_DELETE, DELETE_TIMESHEET,
  EMPLOYEE_AUTOCOMPLETE, DOWNLOAD_TIMESHEET, FETCH_SUBACCOUNTS, UNAUTH_USER, FETCH_FIELDS_BY_LOC,
  SELECT_GANG_FOR_TIMESHEET, SELECT_GANG_FOR_GANG_TIMESHEET, FETCH_BLOCKS_BY_FIELD, FETCH_MY_EMPLOYEES_TIMESHEET,
  ADD_NEW_GANG_INFO_ROW, REMOVE_HARVEST_FROM_EDIT_TIMESHEET, ADD_HARVEST_TO_EDIT_TIMESHEET, FETCH_EMPLOYEE_TIMESHEETS,
  SELECT_COST_CENTER_BY_LOCATION, EDIT_WORK_RECORD, EDIT_WORK_IN_EDIT_TIMESHEET, EDIT_WORK_RECORD_EDIT_TS,
  ADD_WORK_TO_GANG_TIMESHEET, EDIT_WORK_IN_GANG_TIMESHEET, EDIT_GANG_WORK_RECORD, DELETE_WORK_IN_GANG_TIMESHEET,
  SELECT_HARVEST_TO_EDIT, EDIT_HARVEST
} from '../actions/types';

const initState = {
  employees: [], // constants pulled from db
  myEmployees: [], // Specific employees that are managed by a given user
  fields: [], // constants pulled from db
  blocks: [], // constants pulled from db
  costCenters: {
    all: [],
    displaying: []
  }, // constants pulled from db
  details: {
    all: [],
    displaying: []
  },
  paycodes: [],
  subAccounts: [], // constants pulled from db
  addedEmployees: [], // Any employees added to a timesheet
  employeeTS: { // Full employee TS with secondary TS's; to be applied to all employees added to list
    indMacField: [],
    secondaryTS: {
      workPerformed: [],
      editWork: {}
    },
  },
  gangTS: { // For gang timesheets
    timesheet: {
      emps: []
    },
    addedGangEmployees: [],
    addedGangInfoRow: [], // Contains objects with eids as keys, count of rows per employee
    gangWorkPerformed: [],
    editWork: {}
  },
  timesheets: [], // All timesheets fetched to view; fetched based on date
  myTimesheets: {
    date: moment().format('YYYY-MM-DD'),
    timesheets: []
  },
  employeeTimesheets: {
    employee: {},
    timesheets: []
  },
  edit: {
    employee: {},
    ts: {
      timesheet: {},
      subTimesheets: [],
      removedWork: [],
      harvestTimesheet: [],
      removedHarvest: [],
      editWork: {},
      editHarvest: {}
    }, // All timesheet information for a particular date
    timesheetDates: [] // All timesheets submitted
  },
  delete: {
    employee: {},
    timesheets: []
  },
  download: ''
};

export default function TimesheetReducer(state=initState, action) {
  switch (action.type) {
    case FETCH_TIMESHEET_DATA:
      return { ...state,
        employees: action.payload.employees,
        myEmployees: action.payload.myEmployees,
        costCenters: {
          all: action.payload.costCenters,
          displaying: action.payload.costCenters,
        },
        details: {
          all: action.payload.details,
          displaying: action.payload.details
        },
        paycodes: action.payload.paycodes
      };
    case ADD_EMPLOYEE_TO_TIMESHEET:
      // Make sure users cannot add duplicates
      if ( state.addedEmployees.find((item) => item.eid === action.payload.eid) ) return state;
      return { ...state, addedEmployees: state.addedEmployees.concat(action.payload) };
    case EMPLOYEE_AUTOCOMPLETE:
      // Make sure not in list
      if (state.addedEmployees.find((item) => item.eid === action.payload.eid)) return state;
      if (state.gangTS.addedGangEmployees.find((item) => item.eid === action.payload.eid)) return state;

      /* Employee timesheet has already been submitted, return original state */
      //if (action.payload.status) return state;

      return handleAutocomplete(action.id, action.payload, state);
    case REMOVE_EMPLOYEE_FROM_TIMESHEET:
      return { ...state, addedEmployees: state.addedEmployees.filter((item) => item.eid !== action.payload.eid) };
    case ADD_WORK_TO_TIMESHEET:
      return { ...state, employeeTS: { ...state.employeeTS, secondaryTS: {
        workPerformed: state.employeeTS.secondaryTS.workPerformed.concat(action.payload)
      } } };
    case REMOVE_WORK_FROM_TIMESHEET:
      return { ...state, employeeTS: { ...state.employeeTS, secondaryTS: {
        workPerformed: state.employeeTS.secondaryTS.workPerformed.filter((w) => w.workId !== action.payload)
      } } };
    case ADD_EMPLOYEE_TO_GANG_TIMESHEET:
      if ( state.gangTS.addedGangEmployees.find((item) => item.eid === action.payload.eid) ) return { ...state };
      return { ...state, gangTS: { timesheet: state.gangTS.timesheet,
        addedGangEmployees: state.gangTS.addedGangEmployees.concat(action.payload)
      } };
    case REMOVE_EMPLOYEE_FROM_GANG_TIMESHEET:
      return { ...state, gangTS: { ...state.gangTS,
        timesheet: state.gangTS.timesheet,
        addedGangEmployees: state.gangTS.addedGangEmployees.filter((e) => e.eid !== action.payload),
        addedGangInfoRow: state.gangTS.addedGangInfoRow.filter((e) => e.eid !== action.payload)
      } };
    case PREPARE_REG_TIMESHEET:
      return { ...state, employeeTS: action.payload };
    case SUBMIT_REG_TIMESHEET:
      if (!action.payload) return state;
      return { ...state, addedEmployees: [], employeeTS: { indMacField: [], secondaryTS: { workPerformed: [] } } };
    case PREPARE_GANG_TIMESHEET:
      let _emps = action.payload.emps;
      // Add actual grades to submit
      for (const e of state.gangTS.addedGangEmployees) {
        _emps.map((emp) => {
          if (emp.eid === e.eid) {
            emp.grade = e.grade;
            //if (Number(emp.grade) > 2) emp.grade = 2; // Removed 4.14.22
            emp.firstName = e.firstName;
            emp.lastName = e.lastName;
          }
          return emp;
        });
      }
      return { ...state, gangTS: { ...state.gangTS,
        timesheet: {
          timesheetID: action.payload.timesheetID,
          submitter: action.payload.submitter,
          location: action.payload.location,
          date: action.payload.date,
          round: action.payload.round,
          emps: _emps
        }
      } };
    case SUBMIT_GANG_TIMESHEET:
      return { ...state, employees: action.payload,
        gangTS: {
          timesheet: {
            emps: []
          },
          addedGangEmployees: [],
          addedGangInfoRow: [],
          gangWorkPerformed: [],
          editWork: {}
        }
      };
    case FETCH_TIMESHEETS:
      return { ...state, timesheets: action.payload,
        employeeTimesheets: {
          employee: {},
          timesheets: []
        }
      };
    case FETCH_EMPLOYEE_TIMESHEETS:
      return { ...state,
        employeeTimesheets: {
          employee: action.payload.employee[0],
          timesheets: action.payload.timesheets
        },
        timesheets: []
      };
    case GET_TIMESHEET_DATES_TO_EDIT:
      return { ...state,
        edit: { ...state.edit,
          employee: action.employee,
          timesheetDates: action.payload
        }
      };
    case FETCH_TIMESHEET_TO_EDIT:
      return { ...state,
        costCenters: { ...state.costCenters,
          displaying: state.costCenters.all.filter((cc) => cc.location === action.payload.timesheet.location)
        },
        fields: action.fields,
        subAccounts: action.payload.subAccounts,
        edit: { ...state.edit,
          ts: action.payload
        }
      };
    case REMOVE_WORK_FROM_EDIT_TIMESHEET:
      let droppedWork = state.edit.ts.subTimesheets.find((item) => item.id === action.payload);
      return { ...state,
        edit: { ...state.edit,
          ts: { ...state.edit.ts,
            subTimesheets: state.edit.ts.subTimesheets.filter((item) => item.id !== action.payload),
            removedWork: state.edit.ts.removedWork.concat(droppedWork)
          }
        }
      };
    case ADD_WORK_TO_EDIT_TIMESHEET:
      return { ...state,
        edit: { ...state.edit,
          ts: { ...state.edit.ts,
            subTimesheets: state.edit.ts.subTimesheets.concat(action.payload)
          }
        }
      }
    case PREPARE_EDIT_TIMESHEET:
      return { ...state,
        edit: { ...state.edit,
          ts: action.payload
        }
      }
    case SUBMIT_EDIT_TIMESHEET:
      return { ...state,
        edit: { ...state.edit,
          ts: {
            timesheet: {},
            subTimesheets: [],
            removedWork: [],
            harvestTimesheet: [],
            removedHarvest: []
          }
        }
      };
    case FETCH_TIMESHEETS_TO_DELETE:
      return { ...state,
        delete: {
          employee: action.employee,
          timesheets: action.payload
        }
      }
    case DELETE_TIMESHEET:
      return {
        ...state,
        delete: {
          employee: {},
          timesheets: []
        }
      };
    case DOWNLOAD_TIMESHEET:
      return {
        ...state
      };
    case FETCH_SUBACCOUNTS:
      return { ...state, subAccounts: action.payload.subAccounts };
    case FETCH_FIELDS_BY_LOC:
      return { ...state, fields: action.payload };
    case FETCH_BLOCKS_BY_FIELD:
      /* Check for possible duplicates */
      for (const block of state.blocks) {
        if (action.payload.find((b) => b.block === block.block)) return state;
      }
      return { ...state, blocks: state.blocks.concat(action.payload) };
    case SELECT_GANG_FOR_TIMESHEET:
      // TODO: Make sure you cant add duplicates
      return { ...state,
        addedEmployees: action.payload.emps//state.addedEmployees.concat(action.payload.emps)
      };
    case SELECT_GANG_FOR_GANG_TIMESHEET:
      // TODO: Make sure you cant add duplicates
      return { ...state,
        gangTS: {
          ...state.gangTS,
          addedGangEmployees: action.payload.emps//state.gangTS.addedGangEmployees.concat(action.payload.emps)
        }
      };
    case FETCH_MY_EMPLOYEES_TIMESHEET:
      return { ...state,
        myEmployees: state.employees.filter((e) => e.managerId === action.payload)
      };
    case ADD_NEW_GANG_INFO_ROW:
      // Check if employee has a form row added
      let emp = state.gangTS.addedGangInfoRow.find((e) => {
        if (action.payload === e.eid) return e;
        else return !e;
      });
      // If an employee exists in addedGangInfoRow, update the count of forms and return new state
      if (emp) {
        let addedGangInfoRow = state.gangTS.addedGangInfoRow.map((e) => {
          if (e.eid === emp.eid) {
            e.count.push(1);
          }
          return e;
        });
        return { ...state,
          gangTS: { ...state.gangTS,
            addedGangInfoRow
          }
        };
      }

      return { ...state,
        gangTS: { ...state.gangTS,
          addedGangInfoRow: state.gangTS.addedGangInfoRow.concat({ eid: action.payload, count: [1] })
        }
      };
    case REMOVE_HARVEST_FROM_EDIT_TIMESHEET:
      return { ...state,
        edit: { ...state.edit,
          ts: { ...state.edit.ts,
            harvestTimesheet: state.edit.ts.harvestTimesheet.filter((h) => h.id !== action.payload),
            removedHarvest: state.edit.ts.removedHarvest.concat({ id: action.payload })
          }
        }
      };
    case ADD_HARVEST_TO_EDIT_TIMESHEET:
      return { ...state,
        edit: { ...state.edit,
          ts: { ...state.edit.ts,
            harvestTimesheet: state.edit.ts.harvestTimesheet.concat(action.payload)
          }
        }
      }
    case SELECT_COST_CENTER_BY_LOCATION:
      return { ...state,
        costCenters: { ...state.costCenters,
          displaying: action.payload === '71' 
            ? state.costCenters.all.filter((cc) => cc.location === action.payload)
            : action.payload === '72'
            ? state.costCenters.all.filter((cc) => cc.location === action.payload)
            : state.costCenters.all.filter((cc) => cc.location === '71')

        }
      };
    case EDIT_WORK_IN_TIMESHEET:
      return { ...state,
        employeeTS: {
          ...state.employeeTS.indMacField,
          secondaryTS: {
            workPerformed: [...state.employeeTS.secondaryTS.workPerformed],
            editWork: action.payload
          }
        }
      };
    case EDIT_WORK_RECORD:
      let workId = state.employeeTS.secondaryTS.editWork.workId;
      return { ...state,
        employeeTS: {
          ...state.employeeTS.indMacField,
          secondaryTS: {
            workPerformed: state.employeeTS.secondaryTS.workPerformed.filter((w) => w.workId !== workId).concat(action.payload),
            editWork: {}
          }
        }
      };
    case EDIT_WORK_IN_EDIT_TIMESHEET:
      return { ...state,
        edit: {
          ...state.edit,
          ts: {
            ...state.edit.ts,
            editWork: action.payload
          }
        }
      };
    case EDIT_WORK_RECORD_EDIT_TS:
      let id = state.edit.ts.editWork.id;
      action.payload.id = id;
      return { ...state,
        edit: {
          ...state.edit,
          ts: {
            ...state.edit.ts,
            subTimesheets: state.edit.ts.subTimesheets.filter((w) => w.id !== id).concat(action.payload),
            editWork: {}
          }
        }
      };
    case ADD_WORK_TO_GANG_TIMESHEET:
      return { ...state,
        gangTS: {
          ...state.gangTS,
          gangWorkPerformed: state.gangTS.gangWorkPerformed.concat(action.payload)
        }
      };
    case EDIT_WORK_IN_GANG_TIMESHEET:
      return { ...state,
        gangTS: {
          ...state.gangTS,
          editWork: action.payload
        }
      };
    case EDIT_GANG_WORK_RECORD:
      return { ...state,
        gangTS: {
          ...state.gangTS,
          gangWorkPerformed: state.gangTS.gangWorkPerformed.filter((w) => w.workId !== state.gangTS.editWork.workId).concat(action.payload),
          editWork: {}
        }
      };
    case SELECT_HARVEST_TO_EDIT:
      return { ...state,
        edit: { ...state.edit,
          ts: { ...state.edit.ts,
            editHarvest: action.payload
          }
        }
      }
    case EDIT_HARVEST:
      return { ...state,
        edit: { ...state.edit,
          ts: { ...state.edit.ts,
            harvestTimesheet: state.edit.ts.harvestTimesheet.filter((w) => w.id !== action.payload.id).concat(action.payload),
            editHarvest: {}
          }
        }
      }
    case DELETE_WORK_IN_GANG_TIMESHEET:
      return { ...state,
        gangTS: {
          ...state.gangTS,
          gangWorkPerformed: state.gangTS.gangWorkPerformed.filter((w) => w.workId !== action.payload)
        }
      };
    case FETCH_MY_TIMESHEETS:
      return { ...state,
        myTimesheets: {
          timesheets: action.payload,
          date: action.date
        }
      };
    case UNAUTH_USER:
      return initState;
    default:
      return state;
  }
}

function handleAutocomplete(id, emp, state) {
  switch (id) {
    case 'employee-reg-ts-list':
      return { ...state, addedEmployees: state.addedEmployees.concat(emp) };
    case 'employee-gang-ts-list':
      return { ...state,
        gangTS: { ...state.gangTS,
          addedGangEmployees: state.gangTS.addedGangEmployees.concat(emp)
        }
      };
    default:
      return state;
  }
}

/* END */



// WEBPACK FOOTER //
// ./src/reducers/timesheet_reducer.js